import { resolveQuery, type ResolvedQuery } from './query';
import { resolveLangPath } from './langPath';
import { getSafeSearchParams } from './searchParams';
import type { ResolvedConfigs } from '@/modules/config/types';
import { FEATURES } from './features';

export type LayoutFixtureName = 'valid' | 'broken';
export type AnalyticsMode = 'dev' | 'production';
export type ThemeName = 'light' | 'dark' | 'grey';
export type PaymentApiMode = 'commerce' | 'legacy';
export type PaymentTestBuild = 'app' | 'payments-test';

export interface AppConfig {
  environment: string;
  layoutFixture: LayoutFixtureName;
  sentryDsn?: string;
  analyticsMode: AnalyticsMode;
  analyticsApiUrl?: string;
  analyticsWriteKey?: string;
  analyticsHost?: string;
  facebookPixelId?: string;
  googleTagId?: string;
  tiktokPixelId?: string;
  pinterestTagId?: string;
  amplitudeApiKey?: string;
  snapchatPixelId?: string;
  intercom?: { appId: string; apiBase: string };
  posthog?: { apiKey: string; apiHost: string };
  /** VWO ↔ Amplitude integration is opt-in (marked temporary in WebQuiz). */
  vwoEnabled: boolean;
  productName: 'fit';
  onboardingVariant: string;
  query: ResolvedQuery;
  /** Result of `resolveRemoteConfigs`; filled in App before the quiz renders. */
  remoteConfigs: ResolvedConfigs;
  amplitudeExperimentKey?: string;
  firebase?: {
    apiKey?: string;
    authDomain?: string;
    projectId?: string;
    storageBucket?: string;
    messagingSenderId?: string;
    appId?: string;
    measurementId?: string;
  };
  ssoClientIds: { google?: string; microsoft?: string; yahoo?: string };
  apiUrl?: string;
  theme: ThemeName;
  /** Language: `/<lang>/` path prefix, else whitelisted `?lang=`, else `en`. */
  lang: string;
  /** Router base — `'/ru'` when the language came from the path, `''` otherwise. */
  basePath: string;
  /** Filtered query params of the current URL (see `config/searchParams.ts`). */
  search: URLSearchParams;
  apiMode?: PaymentApiMode;
  paymentApiUrl?: string;
  stripePublishableKey?: string;
  applePayDomain?: string;
  /** Sign in with Apple services id (register screen). */
  appleSignInClientId: string;
  paymentReturnUrl?: string;
  paymentTestBuild?: PaymentTestBuild;
  dOfferSlug?: string;
  instantAppUrl?: string;
  layoutChunksEnabled?: boolean;
  layoutChunkConcurrency?: number;
}

const asTheme = (value: string | undefined): ThemeName => (value === 'dark' || value === 'grey' ? value : 'light');

const search = getSafeSearchParams();
const langPath = resolveLangPath(typeof window === 'undefined' ? '/' : window.location?.pathname, search.get('lang'));
const environment = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;
const production = environment === 'production';
const apiUrl = import.meta.env.VITE_API_URL || undefined;
const query = resolveQuery(search, undefined, { production });

export const appConfig: AppConfig = {
  environment,
  // Unit tests pass 'broken' via their own config.
  layoutFixture: 'valid',
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || undefined,
  // Pixels and prod sampling only in production.
  analyticsMode: production ? 'production' : 'dev',
  analyticsApiUrl: apiUrl,
  analyticsWriteKey: import.meta.env.VITE_ANALYTICS_WRITE_KEY || undefined,
  analyticsHost: import.meta.env.VITE_ANALYTICS_HOST || undefined,
  facebookPixelId: import.meta.env.VITE_FB_PIXEL_FIT || undefined,
  googleTagId: import.meta.env.VITE_GOOGLE_TAG_KEY_FIT || undefined,
  tiktokPixelId: import.meta.env.VITE_TIKTOK_FIT || undefined,
  pinterestTagId: import.meta.env.VITE_PINTEREST_FIT || undefined,
  amplitudeApiKey: import.meta.env.VITE_AMPLITUDE_FIT || undefined,
  snapchatPixelId: import.meta.env.VITE_SNAPCHAT_FIT || undefined,
  intercom:
    import.meta.env.VITE_INTERCOM_APP_ID
      ? { appId: import.meta.env.VITE_INTERCOM_APP_ID, apiBase: FEATURES.intercomApiBase }
      : undefined,
  // PostHog needs key AND host: without host posthog-js goes to US cloud, without key init hangs.
  posthog:
    import.meta.env.VITE_POSTHOG_FIT && import.meta.env.VITE_POSTHOG_HOST
      ? { apiKey: import.meta.env.VITE_POSTHOG_FIT, apiHost: import.meta.env.VITE_POSTHOG_HOST }
      : undefined,
  vwoEnabled: FEATURES.vwoAmplitudeBridge,
  productName: 'fit',
  onboardingVariant: query.variant,
  query,
  remoteConfigs: { url: {}, amplitude: null, firebase: null, metadata: {} },
  // WebQuiz uses the analytics Amplitude key for Experiment too, so a separate key is optional.
  amplitudeExperimentKey:
    import.meta.env.VITE_AMPLITUDE_EXPERIMENT_KEY_FIT || import.meta.env.VITE_AMPLITUDE_FIT || undefined,
  firebase: import.meta.env.VITE_FIREBASE_FIT_API_KEY
    ? {
        apiKey: import.meta.env.VITE_FIREBASE_FIT_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_FIT_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_FIT_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_FIT_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_FIT_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_FIT_APP_ID,
        measurementId: import.meta.env.VITE_FIREBASE_FIT_MEASUREMENT_ID,
      }
    : undefined,
  ssoClientIds: {
    google: import.meta.env.VITE_SSO_GOOGLE_CLIENT_ID || undefined,
    microsoft: import.meta.env.VITE_SSO_MICROSOFT_CLIENT_ID || undefined,
    yahoo: import.meta.env.VITE_SSO_YAHOO_CLIENT_ID || undefined,
  },
  apiUrl,
  theme: asTheme(search.get('theme') || FEATURES.theme),
  lang: langPath.lang,
  basePath: langPath.basePath,
  search,
  apiMode: FEATURES.paymentApiMode,
  paymentApiUrl: apiUrl,
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY_FIT || undefined,
  applePayDomain: window.location.hostname,
  appleSignInClientId: import.meta.env.VITE_APPLE_SIGN_IN_CLIENT_ID || 'com.myorganicapps.organicfit.webauth',
  paymentReturnUrl: window.location.href,
  paymentTestBuild: import.meta.env.VITE_PAYMENT_TEST_BUILD === 'true' ? 'payments-test' : 'app',
  dOfferSlug: FEATURES.dOfferSlug,
  instantAppUrl: import.meta.env.VITE_INSTANT_APP_URL || undefined,
  layoutChunksEnabled: FEATURES.layoutChunksEnabled,
  layoutChunkConcurrency: FEATURES.layoutChunkConcurrency,
};

/** Applies remote config before the quiz mounts: Amplitude → Firebase variant overrides query/env. */
export const applyRemoteConfigs = (config: AppConfig, resolved: ResolvedConfigs): AppConfig => {
  config.remoteConfigs = resolved;
  const remote = resolved.amplitude
    ? (['amplitude', resolved.amplitude] as const)
    : resolved.firebase
      ? (['firebase', resolved.firebase] as const)
      : null;
  const variant = remote?.[1].onboardingProperties.variant?.trim();
  if (remote && variant) {
    config.onboardingVariant = variant;
    config.query = { ...config.query, variant, variantSource: remote[0] };
  }
  return config;
};
